import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "../root";
import { ToastService } from "../../services/toast/toast.service";
import { NotificationService } from "../../services/notifications/notification.service";
import { NotificationActions } from "./notification.actions";
import { switchMap, map, catchError, of, withLatestFrom } from "rxjs";
import { CameraActions } from "../camera/camera.actions";
import { ToastType } from "../../models";
import { selectUser } from "../user/user.selectors";
import { IRecipient } from "./notification.state";
import { selectWindowSelectedAlerts } from "../alert/alert.selectors";


@Injectable()
export class NotificationEffects {
    private actions$ = inject(Actions);
    private store$ = inject(Store<AppState>);
    private notificationService = inject(NotificationService);
    private toastService = inject(ToastService);

    loadUserRecipients$ = createEffect(() => this.actions$.pipe(
        ofType(NotificationActions.loadUsersRecipients),
        withLatestFrom(this.store$.select(selectUser)),
        switchMap(([action, user]) => {
            if (user == null) {
                this.toastService.showToast("Failed to load recipients", ToastType.ERROR)
                return of(NotificationActions.loadRecipientsFailure({ error: Error("Invalid user") }))
            }
            return of(NotificationActions.loadRecipients({ organisationId: user.organisation_id }))
        })
    ));

    loadRecipients$ = createEffect(() => this.actions$.pipe(
        ofType(NotificationActions.loadRecipients),
        switchMap((action) => {
            return this.notificationService.getRecipients(action.organisationId).pipe(
                map(response => NotificationActions.loadRecipientsSuccess({ recipients: response as IRecipient[] })),
                catchError(error => {
                    this.toastService.showToast("Failed to load recipients", ToastType.ERROR)
                    return of(NotificationActions.loadRecipientsFailure({ error }))
                })
            );
        })
    ));

    sendNotification$ = createEffect(() => this.actions$.pipe(
        ofType(NotificationActions.sendNotification),
        switchMap((action) => {
            return this.notificationService.sendNotification(action.recipients.map(e => e.id), action.alerts.map(e => e.id)).pipe(
                map(response => {
                    this.toastService.showToast("Notifications sent", ToastType.SUCCESS)
                    return NotificationActions.sendNotificationSuccess()
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to send notifications", ToastType.ERROR)
                    return of(NotificationActions.sendNotificationFailure({ error }))
                })
            );
        })
    ));

    deleteRecipient$ = createEffect(() => this.actions$.pipe(
        ofType(NotificationActions.deleteRecipient),
        switchMap((action) => {
            return this.notificationService.deleteRecipient(action.recipient).pipe(
                map(response => {
                    this.toastService.showToast("Deleted recipient successfully", ToastType.SUCCESS)
                    return NotificationActions.deleteRecipientSuccess({ recipient: action.recipient })
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to delete recipient", ToastType.ERROR)
                    return of(NotificationActions.deleteRecipientFailure({ recipient: action.recipient, error }))
                })
            );
        })
    ));

    updateRecipient$ = createEffect(() => this.actions$.pipe(
        ofType(NotificationActions.updateRecipient),
        switchMap((action) => {
            return this.notificationService.updateRecipient(action.recipient).pipe(
                map(response => {
                    this.toastService.showToast("Updated recipient successfully", ToastType.SUCCESS)
                    return NotificationActions.updateRecipientSuccess({ recipient: response })
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to update recipient", ToastType.ERROR)
                    return of(NotificationActions.updateRecipientFailure({ recipient: action.recipient, error }))
                })
            );
        })
    ));

    createRecipient$ = createEffect(() => this.actions$.pipe(
        ofType(NotificationActions.createRecipient),
        switchMap((action) => {
            return this.notificationService.createRecipient(action.recipient).pipe(
                map(response => {
                    this.toastService.showToast("Created recipient successfully", ToastType.SUCCESS)
                    return NotificationActions.createRecipientSuccess({ recipient: response })
                }),
                catchError(error => {
                    this.toastService.showToast("Failed to create recipient", ToastType.ERROR)
                    return of(NotificationActions.createRecipientFailure({ recipient: action.recipient, error }))
                })
            );
        })
    ));

    // region Storage
    // Logic for storing information so that it is available on reloads


    //endregion

}